/**
 * The phone dropdown's country list, from PRHS instead of the module's bundle.
 *
 * The module ships 47 countries and exposes a `getCountryList` option whose
 * contract is that the consumer owns the fetch. PRHS has served the real list at
 * `GET /v1/partners/countries` since the same week, derived per-partner from GES
 * internationalization data — roughly 200 countries. Nothing connected the two,
 * and because the module falls back to its bundle silently, the page showed a
 * plausible-looking list that was simply wrong for anyone outside those 47.
 *
 * PRHS returns codes, not names: `{ countryCode, callingCode, flagUrl,
 * supportStatus }`. Names are resolved here through `Intl.DisplayNames`, which
 * is why the mapping is a function of the locale and has to be re-run when the
 * language dropdown changes.
 */

/**
 * Same bound as the other PRHS preflights in Home. The list is fetched during
 * the module's open(), so a stalled request holds up the form; on timeout the
 * abort reads as "no list" and the module falls back to its bundle.
 */
const COUNTRY_LIST_TIMEOUT_MS = 8000;

/** PRHS calling codes arrive `+`-prefixed; anything else can't be dialled. */
const CALLING_CODE_RE = /^\+\d+$/;

/** ISO alpha-2, plus the CLDR codes PRHS adds (AC, TA, EA, IC, CP, XK). */
const REGION_RE = /^[A-Za-z]{2}$/;

/** Offset from ASCII "A" to the regional indicator symbol letters. */
const REGIONAL_INDICATOR_A = 0x1f1e6;
const ASCII_A = 65;

let cachedCountriesPromise = null;

async function fetchPartnerCountries(url, bearer) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), COUNTRY_LIST_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${bearer}` },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const body = await res.json();
    return Array.isArray(body?.countries) ? body.countries : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Load the partner's country list, memoized for the page lifetime.
 *
 * The list is per-partner reference data that cannot change mid-session, and it
 * is read twice — once when the module opens, and again on every locale switch
 * to re-localize the names. Memoizing keeps the second read off the network, so
 * changing language never puts a request in the middle of a verification.
 *
 * Failures are not cached: a transient blip on the first call would otherwise
 * pin the session to the module's bundled 47 for as long as the tab is open.
 *
 * @param {string} url Fully-resolved `/v1/partners/countries` URL.
 * @param {string} bearer Starfleet id_token.
 * @returns {Promise<Array<object>|null>} Raw PRHS entries, or null if
 *   unavailable — callers treat null as "let the module use its bundle".
 */
export function loadPartnerCountries(url, bearer) {
  if (cachedCountriesPromise) return cachedCountriesPromise;
  if (!url || !bearer) return Promise.resolve(null);
  cachedCountriesPromise = fetchPartnerCountries(url, bearer).then(
    (list) => {
      if (list == null) cachedCountriesPromise = null;
      return list;
    },
    (err) => {
      cachedCountriesPromise = null;
      throw err;
    }
  );
  return cachedCountriesPromise;
}

/**
 * The flag emoji for a region, e.g. "US" -> "🇺🇸".
 *
 * Only ever rendered when the module's sprite has no artwork for the region,
 * which after the sprite grew to cover every region in the flag-icons set means
 * a code CLDR carries and flag-icons does not. Windows draws the emoji as the
 * two letters ("AC"), which is the failure the sprite was built to fix — but for
 * a region with no artwork the alternative is empty space, and the letters at
 * least name the place. The name is beside it either way.
 */
function flagEmoji(region) {
  return String.fromCodePoint(
    ...[...region.toUpperCase()].map(
      (ch) => REGIONAL_INDICATOR_A + (ch.codePointAt(0) - ASCII_A)
    )
  );
}

/**
 * The localized country name for a region, falling back to the code itself.
 *
 * `Intl.DisplayNames` already falls back to the code for a region it doesn't
 * know, so the try/catch is for the constructor: it throws on a locale tag it
 * cannot parse, and the locale here comes from a URL parameter.
 */
function makeRegionNamer(locale) {
  let displayNames;
  try {
    displayNames = new Intl.DisplayNames([locale || 'en'], { type: 'region' });
  } catch {
    try {
      displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      displayNames = null;
    }
  }
  return (region) => {
    if (!displayNames) return region;
    try {
      return displayNames.of(region) || region;
    } catch {
      return region;
    }
  };
}

/**
 * The localized name for a single region, by the same route the list uses.
 *
 * Exported for the `defaultCountryCode` the module is constructed with, which it
 * resolves by matching country NAME against the list. Deriving that name any
 * other way is how it silently stops matching.
 *
 * @param {string} region ISO alpha-2 region code.
 * @param {string} locale Active UI locale.
 * @returns {string} Localized name, or the region code when there is no name.
 */
export function regionDisplayName(region, locale) {
  const code = String(region ?? '')
    .trim()
    .toUpperCase();
  if (!REGION_RE.test(code)) return code;
  return makeRegionNamer(locale)(code);
}

/**
 * Compare country names the way the user's language orders them.
 *
 * Same reason as the namer above for the try/catch: `Intl.Collator` throws on a
 * locale tag it cannot parse. A plain string compare is the last resort, which
 * gets the ASCII names right and only misplaces accented ones.
 */
function makeNameComparator(locale) {
  try {
    const collator = new Intl.Collator(locale || 'en');
    return (a, b) => collator.compare(a.country, b.country);
  } catch {
    return (a, b) => (a.country < b.country ? -1 : a.country > b.country ? 1 : 0);
  }
}

/**
 * Map PRHS entries into the module's `{ code, country, flag, region }` shape.
 *
 * Notable choices:
 *
 * - `NOT_SUPPORTED` entries stay ordinary selectable countries. The API's status
 *   exists so the dropdown can grey them out, and the module has no such state,
 *   but greying them out is not what we want anyway: people register phones from
 *   countries the partner does not otherwise support. The divergence is
 *   deliberate and worth settling with the API team rather than leaving implied.
 *
 * - `flagUrl` is ignored. PRHS points at flagcdn.com, and the sprite covers
 *   every region, so keeping the artwork bundled avoids a third-party request
 *   from an authenticated page.
 *
 * - Names are made unique. `normalizeCountryList` drops duplicates by name and
 *   keeps the first, so two regions sharing a display name would silently cost
 *   the user a country; a collision is disambiguated by its code instead.
 *
 * @param {Array<object>|null|undefined} entries Raw PRHS entries.
 * @param {string} locale Active UI locale, e.g. "pt-BR".
 * @returns {Array<{code: string, country: string, flag: string, region: string}>}
 *   Empty when there is nothing usable, which the module reads as "use the
 *   bundled list".
 */
export function toModuleCountryList(entries, locale) {
  if (!Array.isArray(entries)) return [];

  const nameFor = makeRegionNamer(locale);
  const seenRegions = new Set();
  const takenNames = new Set();
  const mapped = [];

  for (const entry of entries) {
    const region = String(entry?.countryCode ?? '')
      .trim()
      .toUpperCase();
    const code = String(entry?.callingCode ?? '').trim();
    // Entries PRHS could not resolve a calling code for arrive with the field
    // omitted. normalizeCountryList would drop them too; doing it here keeps
    // them out of the name-collision bookkeeping below.
    if (!REGION_RE.test(region) || !CALLING_CODE_RE.test(code)) continue;
    if (seenRegions.has(region)) continue;
    seenRegions.add(region);

    const name = nameFor(region);
    const country = takenNames.has(name) ? `${name} (${region})` : name;
    takenNames.add(country);

    mapped.push({ code, country, flag: flagEmoji(region), region });
  }

  // Alphabetical in the user's language, which is the only order that reads as
  // sorted to them: PRHS sorts by ISO code, so a Portuguese user would see
  // Germany ("Alemanha") filed under D. US and Canada are floated back to the
  // top by the module's priorityCountries.
  mapped.sort(makeNameComparator(locale));
  return mapped;
}

/**
 * Test seam: drops the memoized list so each test can install its own fetch
 * mock and observe a fresh load. Not used in production code.
 */
export function __resetPartnerCountriesCache() {
  cachedCountriesPromise = null;
}
